/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Grid, Link, PageGridContainer, Typography, breakpoints } from '@coursera/cds-core';
import { AddIcon } from '@coursera/cds-icons';

import { useThirdPartyOrganization } from 'bundles/admin-dashboard/components/ThirdPartyOrganizationProvider';
import EnterprisePermissionsRequired from 'bundles/enterprise-admin-roles/components/EnterprisePermissionsRequired';
import { TrackedButtonLink } from 'bundles/enterprise-ui/components/Button';

import _t from 'i18n!nls/enterprise-ui';

type Props = {
  orgSlug: string;
  headerText: string | React.ReactNode;
  buttonText: string;
  buttonURL: string;
  buttonAriaLabel?: string;
  buttonTrackingName: string;
  'data-e2e'?: string;
};

const styles = {
  wrapper: () => css`
    background-color: #e9ecf1;
  `,
  container: () => css`
    margin-top: var(--cds-spacing-400);
    margin-bottom: var(--cds-spacing-400);
  `,
  buttonContainer: () => css`
    margin-top: var(--cds-spacing-300);
  `,
  link: () => css`
    ${breakpoints.down('xs')} {
      margin-top: var(--cds-spacing-100);
    }
  `,
  linkText: () => css`
    text-decoration: underline;
  `,
  button: () => css`
    margin-right: var(--cds-spacing-300);
  `,
  childrenContainer: () => css`
    margin-top: var(--cds-spacing-200);
    margin-bottom: var(--cds-spacing-300);
  `,
};

export const WelcomeBanner: React.LegacyFunctionComponentWithChildren<Props> = ({
  orgSlug,
  children,
  headerText,
  buttonText,
  buttonTrackingName,
  buttonURL,
  buttonAriaLabel,
  ...otherProps
}) => {
  const org = useThirdPartyOrganization();
  return (
    <div css={styles.wrapper} data-e2e={otherProps['data-e2e']}>
      <PageGridContainer maxWidth="lg">
        <Grid item xs={12} css={styles.container}>
          <Typography variant="d2semibold">{headerText}</Typography>
          <Grid item css={styles.childrenContainer}>
            {children}
          </Grid>
          <Grid container alignItems="center" direction="row" css={styles.buttonContainer}>
            <TrackedButtonLink
              label={buttonText}
              trackingName={buttonTrackingName}
              variant="primary"
              size="medium"
              to={buttonURL}
              aria-label={buttonAriaLabel || buttonText}
              css={styles.button}
            />
            <EnterprisePermissionsRequired domain="ORGANIZATION" domainId={org.id} verb="MANAGE" subdomain="ADMINS">
              <Link
                icon={<AddIcon />}
                data-e2e="add-new-admin"
                iconPosition="before"
                variant="standard"
                typographyVariant="bodyPrimary"
                href={`/o/${orgSlug}/admin/users/permissions`}
                css={styles.link}
              >
                <span css={styles.linkText}>{_t('Add new admin')}</span>
              </Link>
            </EnterprisePermissionsRequired>
          </Grid>
        </Grid>
      </PageGridContainer>
    </div>
  );
};

export default WelcomeBanner;
